import {
    StageReviewInfo,
    StageReviewInfos,
    AnswerCoachList,
    QuestionCoachList,
    MetricData,
} from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { BoldText, DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import SvgIcon from '@/components/SvgIcon';
import React, { useState } from 'react';
import { overlay } from 'overlay-kit';
import ModalAllReview from './ModalAllReview';

type Props = {
    stageReviewInfos?: StageReviewInfos;
};

const stageConfig = [
    { key: 'first_class' as const, label: '첫 회차', badgeNumber: 1 },
    { key: 'middle_class' as const, label: '중간 회차', badgeNumber: 2 },
    { key: 'final_class' as const, label: '마지막 회차', badgeNumber: 3 },
];

function FeedInstructorReviewsStageReviewInfos({ stageReviewInfos }: Props) {
    if (!stageReviewInfos) {
        return null;
    }

    const openModal = (
        config: (typeof stageConfig)[number],
        stageData: StageReviewInfo,
        metricData: MetricData[],
        initialSelection?: {
            questionCode?: string;
            questionSubject?: string;
            tab?: 'all' | 'positive' | 'negative' | 'custom';
            answerCode?: string;
            answerContent?: string;
        }
    ) => {
        overlay.open(({ unmount }) => (
            <ModalAllReview
                unmount={unmount}
                stageData={stageData}
                config={config}
                metricData={metricData}
                initialSelection={initialSelection}
            />
        ));
    };

    function getMetricData(stageData: StageReviewInfo): MetricData[] {
        const coachIdAndCoachInfoMap = new Map<string, QuestionCoachList>();
        const coachIdAndCoachAnswerListMap = new Map<string, AnswerCoachList[]>();
        stageData.stageQuestionInfos.forEach((question) => {
            question.questionCoachList.forEach((coach) => {
                coachIdAndCoachInfoMap.set(coach.coachId, coach);
            });
            question.answerCoachList.forEach((answer) => {
                if (coachIdAndCoachAnswerListMap.has(answer.coachId)) {
                    coachIdAndCoachAnswerListMap.get(answer.coachId)?.push(answer);
                } else {
                    coachIdAndCoachAnswerListMap.set(answer.coachId, [answer]);
                }
            });
        });

        const coachIdList = Array.from(coachIdAndCoachInfoMap.keys());

        const coachAnswerCompareMetricData: MetricData[] = coachIdList.map((coachId) => {
            const coachInfo = coachIdAndCoachInfoMap.get(coachId)!;
            const coachAnswerList = coachIdAndCoachAnswerListMap.get(coachId) ?? [];

            const codeValueAndAnswerCountMap = new Map<string, number>();
            coachAnswerList.forEach((answer) => {
                codeValueAndAnswerCountMap.set(`${answer.questionCode}-${answer.answerCode}`, answer.answerCount);
            });

            return {
                ...coachInfo,
                codeValueAndAnswerCountMap,
            };
        });
        return coachAnswerCompareMetricData;
    }

    return (
        <div className="flex flex-col w-full gap-[24px]">
            {stageConfig.map((config) => {
                const stageData = stageReviewInfos[config.key];
                if (!stageData) return null;

                const metricData = getMetricData(stageData);
                return (
                    <StageSection
                        key={config.key}
                        stageData={stageData}
                        label={config.label}
                        badgeNumber={config.badgeNumber}
                        onClickSection={(initialSelection) => openModal(config, stageData, metricData, initialSelection)}
                    />
                );
            })}
        </div>
    );
}

type StageSectionProps = {
    stageData: StageReviewInfo;
    label: string;
    badgeNumber: number;
    onClickSection: (initialSelection?: {
        questionCode?: string;
        questionSubject?: string;
        tab?: 'all' | 'positive' | 'negative' | 'custom';
        answerCode?: string;
        answerContent?: string;
    }) => void;
};

function StageSection({ stageData, label, badgeNumber, onClickSection }: StageSectionProps) {
    const {
        stageAverageRating,
        stageAverageRatingChange,
        stageReviewMemberCount,
        stageAnswerCount,
        stageQuestionRankings,
        stagePositiveAnswerSummary,
        stageNegativeAnswerSummary,
    } = stageData;

    const isIncrease = stageAverageRatingChange > 0;
    const isDecrease = stageAverageRatingChange < 0;
    const changeColor = isIncrease ? colors.GREEN : isDecrease ? colors.ERROR : colors.Text_1;

    const [hoveredPositiveIndex, setHoveredPositiveIndex] = useState<number | null>(null);
    const [hoveredNegativeIndex, setHoveredNegativeIndex] = useState<number | null>(null);

    return (
        <div className="flex flex-col w-full bg-white rounded-[25px] border-[0.5px] border-[#E8E8E8] px-[26px] py-[37px] ">
            {/* 헤더 */}
            <Row onClick={() => onClickSection()} className="flex items-center justify-between cursor-pointer  ">
                <Row className="flex items-center ">
                    {/* 배지 */}
                    <div className="mr-[14px]">
                        <SvgIcon
                            name={`SvgReviewNum${badgeNumber}` as 'SvgReviewNum1' | 'SvgReviewNum2' | 'SvgReviewNum3'}
                            size={26}
                            fill={colors.Text_1}
                        />
                    </div>

                    {/* 회차명 */}
                    <RegularText fontSize={25} lineHeight={32} color={colors.Text_1} letterSpacing={-0.7}>
                        {label}
                    </RegularText>
                </Row>
                <Row className="flex items-center">
                    {/* 별점 */}
                    <Row className="flex items-center mr-[8px] gap-[6px]">
                        <SvgIcon name="SvgStar" fill="#5F8FFD" size={15} />
                        <MediumText fontSize={25} lineHeight={32} color="#191F28" letterSpacing={-0.7}>
                            {stageAverageRating.toFixed(1)}
                        </MediumText>
                    </Row>

                    {/* 1개월 전보다 */}
                    <RegularText
                        fontSize={16}
                        lineHeight={21}
                        color={colors.Text_1}
                        letterSpacing={-0.7}
                        className="mr-[10px]"
                    >
                        1개월 전보다
                    </RegularText>
                    {/* 증감 */}
                    {(isIncrease || isDecrease) && (
                        <Row className="flex items-center mr-[17px]">
                            <SvgIcon
                                name={'SvgSortArrow'}
                                size={12}
                                fill={isIncrease ? colors.GREEN : colors.ERROR}
                                className={`mr-[3px] ${isIncrease ? '' : 'transform rotate-180'}`}
                            />
                            <RegularText fontSize={16} lineHeight={21} color={changeColor} letterSpacing={-0.7}>
                                {Math.abs(stageAverageRatingChange).toFixed(1)}
                            </RegularText>
                        </Row>
                    )}

                    {/* 참여명 */}
                    <Row className="flex items-center gap-[6px] mr-[22px]">
                        <SvgIcon
                            name="SvgCheck"
                            fill={colors.trans}
                            size={10}
                            stroke={colors.Text_1}
                            strokeWidth={1.75}
                        />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.4}>
                            {stageReviewMemberCount.toLocaleString()}명 참여
                        </RegularText>
                    </Row>

                    {/* 의견수 */}
                    <Row className="flex items-center gap-[4px]">
                        <SvgIcon name="SvgLineHistory" fill={colors.trans} size={14} stroke={colors.Text_1} />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                            {stageAnswerCount.toLocaleString()}회 의견
                        </RegularText>
                    </Row>
                </Row>
            </Row>

            <Col className="flex w-full h-[1px] bg-[#E5E5E5] my-[28px]" />

            {/* 컨텐츠: 왼쪽 리뷰 항목 순위 + 오른쪽 좋았어요/아쉬웠어요 */}
            <div className="flex flex-row gap-[44px]">
                {/* 왼쪽: 리뷰 항목 순위 */}
                <div className="flex flex-col flex-1">
                    <Row className="flex items-center gap-[7px] mb-[22px]">
                        <SvgIcon name="SvgGraph" fill="#5F8FFD" size={18} />
                        <RegularText fontSize={15} lineHeight={20} color={'#191F28'} letterSpacing={-0.7}>
                            리뷰 항목 순위
                        </RegularText>
                    </Row>

                    {/* 순위 목록 */}
                    <div className="flex flex-col gap-[20px]">
                        {stageQuestionRankings.slice(0, 4).map((ranking) => (
                            <Row
                                key={ranking.stageQuestionCode}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClickSection({
                                        questionCode: ranking.stageQuestionCode,
                                        questionSubject: ranking.stageQuestionSubject,
                                        tab: 'all',
                                    });
                                }}
                                className={`flex items-center justify-between px-[16px] py-[12px]  rounded-[9px] border-[0.5px] border-solid border-[#E8E8E8] cursor-pointer
                                    ${
                                        ranking.stageQuestionRank === 1
                                            ? 'bg-[#E2F1FF] hover:bg-[#BFE0FF]'
                                            : ranking.stageQuestionRank === 2
                                            ? 'bg-[#F0F7FF] hover:bg-[#BFE0FF]'
                                            : 'bg-[#F9FAFB] hover:bg-[#F0F2F5]'
                                    }
                                    `}
                            >
                                <Row className="flex items-center gap-[12px]">
                                    <MediumText
                                        fontSize={16}
                                        lineHeight={21}
                                        color={ranking.stageQuestionRank === 1 ? colors.primary : '#191F28'}
                                        letterSpacing={-0.7}
                                    >
                                        {ranking.stageQuestionRank}위
                                    </MediumText>
                                    {/* 1위만 MediumText, 2위부터 RegularText */}

                                    {ranking.stageQuestionRank === 1 ? (
                                        <MediumText
                                            fontSize={16}
                                            lineHeight={21}
                                            color={ranking.stageQuestionRank === 1 ? colors.primary : '#191F28'}
                                            letterSpacing={-0.7}
                                        >
                                            {ranking.stageQuestionSubject}
                                        </MediumText>
                                    ) : (
                                        <RegularText
                                            fontSize={16}
                                            lineHeight={21}
                                            color={ranking.stageQuestionRank === 1 ? colors.primary : '#191F28'}
                                            letterSpacing={-0.7}
                                        >
                                            {ranking.stageQuestionSubject}
                                        </RegularText>
                                    )}
                                </Row>
                                <MediumText
                                    fontSize={16}
                                    lineHeight={21}
                                    color={ranking.stageQuestionRank === 1 ? colors.primary : '#191F28'}
                                    letterSpacing={-0.3}
                                >
                                    {ranking.stageQuestionAverageRating.toFixed(1)}
                                </MediumText>
                            </Row>
                        ))}
                    </div>
                </div>

                {/* 오른쪽: 좋았어요 + 아쉬웠어요 */}
                <div className="flex flex-col flex-1 gap-[32px]">
                    {/* 좋았어요 */}
                    <div className="flex flex-col">
                        <Row className="flex items-center mb-[22px]">
                            <SvgIcon name="SvgReviewLike" fill="#00A793" size={16} className="mr-[7px]" />
                            <RegularText
                                fontSize={15}
                                lineHeight={21}
                                color="#191F28"
                                className="mr-[4px]"
                                letterSpacing={-0.3}
                            >
                                좋았어요
                            </RegularText>
                            <MediumText
                                fontSize={15}
                                lineHeight={21}
                                color="#00A793"
                                className="mr-[4px]"
                                letterSpacing={-0.3}
                            >
                                {stagePositiveAnswerSummary.stageAnswerRate.toFixed(0)}%
                            </MediumText>
                            <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.5}>
                                ({stagePositiveAnswerSummary.stageAnswerCount}회)
                            </DemiLightText>
                        </Row>

                        <div className="flex flex-col gap-[12px]">
                            {stagePositiveAnswerSummary.stageAnswerRankings.length > 0 ? (
                                stagePositiveAnswerSummary.stageAnswerRankings.map((answer, index) => (
                                    <Row
                                        key={answer.stageAnswerCode}
                                        onMouseEnter={() => setHoveredPositiveIndex(index)}
                                        onMouseLeave={() => setHoveredPositiveIndex(null)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickSection({
                                                questionCode: answer.stageQuestionCode,
                                                questionSubject: answer.stageQuestionSubject,
                                                tab: 'all',
                                                answerCode: answer.stageAnswerCode,
                                                answerContent: answer.stageAnswerContent,
                                            });
                                        }}
                                        className="flex items-center justify-between px-[16px] py-[10px] rounded-[9px] border-solid border-[0.5px] border-[#E8E8E8] cursor-pointer transition-colors"
                                        style={{
                                            background:
                                                hoveredPositiveIndex === index
                                                    ? '#C3F4DB'
                                                    : `linear-gradient(to right, #E8F9F3 ${answer.stageAnswerRate}%, #F9FAFB ${answer.stageAnswerRate}%)`,
                                        }}
                                    >
                                        <RegularText fontSize={15} lineHeight={20} color="#12A25D" letterSpacing={-0.4}>
                                            {answer.stageAnswerContent}
                                        </RegularText>
                                        <RegularText fontSize={15} lineHeight={20} color="#12A25D" letterSpacing={-0.7}>
                                            {answer.stageAnswerCount}
                                        </RegularText>
                                    </Row>
                                ))
                            ) : (
                                <div className="flex items-center justify-center py-[20px]">
                                    <RegularText fontSize={14} lineHeight={19} color="#C5C6C9">
                                        등록된 의견이 없어요
                                    </RegularText>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 아쉬웠어요 */}
                    <div className="flex flex-col">
                        <Row className="flex items-center mb-[22px]">
                            <SvgIcon name="SvgReviewBad" fill="#FF7173" size={15} className="mr-[8px]" />
                            <RegularText fontSize={15} lineHeight={21} color="#191F28" className="mr-[4px]">
                                아쉬웠어요
                            </RegularText>
                            <MediumText fontSize={15} lineHeight={21} color="#FF7173" className="mr-[4px]">
                                {stageNegativeAnswerSummary.stageAnswerRate.toFixed(0)}%
                            </MediumText>
                            <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1}>
                                ({stageNegativeAnswerSummary.stageAnswerCount}회)
                            </DemiLightText>
                        </Row>

                        <div className="flex flex-col gap-[12px]">
                            {stageNegativeAnswerSummary.stageAnswerRankings.length > 0 ? (
                                stageNegativeAnswerSummary.stageAnswerRankings.map((answer, index) => (
                                    <Row
                                        key={answer.stageAnswerCode}
                                        onMouseEnter={() => setHoveredNegativeIndex(index)}
                                        onMouseLeave={() => setHoveredNegativeIndex(null)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickSection({
                                                questionCode: answer.stageQuestionCode,
                                                questionSubject: answer.stageQuestionSubject,
                                                tab: 'all',
                                                answerCode: answer.stageAnswerCode,
                                                answerContent: answer.stageAnswerContent,
                                            });
                                        }}
                                        className="flex items-center justify-between px-[16px] py-[10px] rounded-[9px] border-solid border-[0.5px] border-[#E8E8E8] cursor-pointer transition-colors"
                                        style={{
                                            background:
                                                hoveredNegativeIndex === index
                                                    ? '#FFC9CB'
                                                    : `linear-gradient(to right, #FFF0F0 ${answer.stageAnswerRate}%, #F9FAFB ${answer.stageAnswerRate}%)`,
                                        }}
                                    >
                                        <RegularText fontSize={14} lineHeight={19} color="#B10E27" letterSpacing={-0.4}>
                                            {answer.stageAnswerContent}
                                        </RegularText>
                                        <RegularText fontSize={14} lineHeight={19} color="#B10E27" letterSpacing={-0.4}>
                                            {answer.stageAnswerCount}
                                        </RegularText>
                                    </Row>
                                ))
                            ) : (
                                <div className="flex items-center justify-center py-[20px]">
                                    <RegularText fontSize={14} lineHeight={19} color="#C5C6C9">
                                        등록된 의견이 없어요
                                    </RegularText>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FeedInstructorReviewsStageReviewInfos;
